/**
 * Kurssicherung (DTG-Register), Mischkurs und Effektivkurs - 1:1-Portierung aus `index.html` (Stand
 * 2026-08-08). Die Rechenwege sind woertlich uebernommen, auch wo sie umstaendlich wirken oder auf
 * den ersten Blick fehlerhaft aussehen; ergaenzt wurden ausschliesslich Typen. Namen und Logik sind
 * unangetastet.
 *
 * ================= Der Umbau: DB-Bindung -> Argument =================
 *
 * Im Original lesen `orderFx`, `fxMix`, `effRate`, `nextFxNo` und `fxWeekBuckets` das globale
 * `DB.fxDeals`; hier bekommen sie die Deals als Argument `deals`. `orderOpenPayments` bekommt
 * zusaetzlich `suppliers`, weil `orderCurrency` (`src/lib/suppliers.ts`) die Lieferantenliste als
 * Argument nimmt (Task 4).
 *
 * `fxWeekBuckets` braucht zusaetzlich `definitionen: StatusDefinitionen` und `orders`: sie ruft
 * `paymentCalendarRows` auf, die ueber `activeReportingOrders` `isSplitStatusOrder` erreicht - siehe
 * unten. Die Stellung von `definitionen` (unmittelbar hinter dem Entitaets-/Hauptparameter, vor
 * weiteren aufgeloesten Datenlisten) folgt der Konvention, die `lib/status.ts` bereits vierfach
 * instanziiert (`actionInfo`, `overviewActionInfo`, `orderStageIndex`, `boardGroupId`) - Entscheid
 * des Controllers zu Task 6, siehe Bericht.
 *
 * ================= activeReportingOrders und paymentCalendarRows liegen NICHT hier =================
 *
 * Beide stehen im Task-Brief unter diesem Modul, liegen aber tatsaechlich in `src/lib/status.ts`:
 * `paymentCalendarRows` braucht `orderListDue` aus `status.ts`, und `status.ts` importiert bereits
 * `lib/payments.ts` (fuer `payAmount` u. a.) - in `lib/payments.ts` ergaebe `paymentCalendarRows`
 * damit den Importzyklus payments -> fx -> payments. `activeReportingOrders` zieht aus demselben
 * Grund mit. Beide werden hier importiert, nicht neu portiert. `fmtUsd` (Zulieferer von
 * `fxWeekChartValue`) liegt aus demselben additiven Grund in `src/lib/format.ts`.
 *
 * ================= fxCalculatorValues: der eine bewusste Eingriff =================
 *
 * Die Vorgabe ist im Original `draft = fxCalculatorDraft || {}` - `fxCalculatorDraft` ist eine
 * globale `let`-Variable fuer den Entwurfszustand der Eingabemaske (`index.html:4596`, Wert `null`).
 * Reiner Oberflaechenzustand; Phase 2b liefert ausschliesslich Fachlogik. Die Vorgabe wird deshalb zu
 * `draft = {}` - bei `fxCalculatorDraft === null` verhaltensgleich (`null || {}` ist `{}`). Der
 * Rumpf bleibt unangetastet. Entscheid des Controllers, siehe Bericht.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `effRate` mischt `m.rate > 0 ? m.rate : (paymentUsdRate(o) || Number(o.mixedUsdRate) ||
 *    Number(o.usdRate) || 0)` - die Klammer um den Alternativzweig ist im Original vorhanden und
 *    aendert die Auswertung nicht, bleibt aber zeichengetreu stehen.
 * 2. `fxWeekBuckets` benutzt ZWEI verschiedene "leere Woche"-Formen: den echten Rueckfall von
 *    `isoWeekInfo` (`{key:'ohne', year:'', week:'', start:'', end:''}`, fuenf Felder) und einen
 *    zweiten, literal im Funktionskoerper stehenden Rueckfall OHNE `year`
 *    (`{key:'ohne', start:'', end:'', week:''}`, vier Felder, andere Feldreihenfolge). Beide Formen
 *    sind woertlich uebernommen, nicht vereinheitlicht.
 * 3. `orderOpenPayments.overdue` ist NICHT durchgehend ein Wahrheitswert: `p.due && p.due < today`
 *    liefert bei leerem `p.due` den leeren String selbst zurueck (Kurzschluss von `&&`), nur bei
 *    gesetztem `p.due` einen echten Wahrheitswert. Woertlich uebernommen, Typ entsprechend
 *    `string | boolean`.
 * 4. `paymentUsdRate`, `fxMix` und `fxCalculatorValues` teilen bzw. gewichten Summen. Die
 *    Auswertungsreihenfolge einer Gleitkommasumme ist ihr Ergebnis - `reduce`-Reihenfolge, Klammerung
 *    und Operandenreihenfolge sind zeichengleich uebernommen.
 * 5. `dealAllocated`/`orderFx` lasen `d.allocations` im Original ungesichert (`d.allocations || []`)
 *    - ein wahrhaftiger Nicht-Array blieb nach `|| []` stehen und `.reduce`/`.filter` warfen. REPARIERT
 *    am 2026-08-11 (Befund 9, Option B) - siehe Kommentar an `dealAllocated`.
 *
 * ================= Notwendige Abhaengigkeiten =================
 *
 * `orderOpenPayments` (index.html:13802) stand in der Planliste von Task 5, wurde von dort aber
 * hierher verschoben: sie ruft `effRate(o)` auf, das ueber `fxMix`/`orderFx` an `DB.fxDeals` haengt.
 * Laege sie in `lib/payments.ts`, entstuende der Importzyklus payments -> fx -> payments (`fx.ts`
 * braucht `payAmount` aus `lib/payments.ts` fuer `paymentUsdRate`). Entscheid des Controllers.
 */
import { addDays, isoWeekInfo, NUM, todayIso, fmtUsd, type IsoWeekInfo } from "./format.ts";
import { isPaid, nextDue, orderTotal, payAmount, type OrderPayment } from "./payments.ts";
import { paymentCalendarRows, type StatusDefinitionen } from "./status.ts";
import { orderCurrency, type Supplier } from "./suppliers.ts";

// ================= Typen =================

// Die Order-Typdeklaration dieses Moduls steht seit Task 1 gebuendelt in `lib/types.ts`
// (Begruendung dort). Re-Export unter dem bisherigen Namen, damit kein bestehender Importpfad bricht.
import type { Order } from "./types.ts";
export type { Order };

/** Ein Eintrag aus `deal.allocations[]`: Zuordnung eines Teilbetrags zu einer Bestellung. */
interface DealAllocation {
  orderId?: unknown;
  usd?: unknown;
  [feld: string]: unknown;
}

/** Ein Eintrag des zentralen DTG-Registers (`DB.fxDeals[]`). */
export interface FxDeal {
  no?: unknown;
  usd?: unknown;
  rate?: unknown;
  date?: unknown;
  allocations?: unknown;
  [feld: string]: unknown;
}

/** Ein an eine Bestellung gebundener Sicherungs-Teilbetrag (Rueckgabe von `orderFx`). */
export interface FxAllocation {
  deal: FxDeal;
  usd: number;
}

/** Summen und Mischkurs der an eine Bestellung gebundenen Sicherungen (Rueckgabe von `fxMix`). */
export interface FxMix {
  usd: number;
  eur: number;
  rate: number;
}

/** Der Entwurfszustand der Mischkurs-Eingabemaske - Eingabe von `fxCalculatorValues`. */
export interface FxCalculatorDraft {
  oldBalance?: unknown;
  oldRate?: unknown;
  newUsd?: unknown;
  newRate?: unknown;
  newBalanceOverride?: unknown;
  oldShareOverride?: unknown;
  newShareOverride?: unknown;
  mixedRateOverride?: unknown;
  weightingOverride?: unknown;
  [feld: string]: unknown;
}

/** Die berechneten (bzw. per Override ersetzten) Werte des Mischkursrechners. */
export interface FxCalculatorValues {
  oldBalance: number;
  oldRate: number;
  newUsd: number;
  newRate: number;
  newBalance: number;
  oldShare: number;
  newShare: number;
  mixedRate: number;
  weightingPct: number;
}

/**
 * Eine Wochen-Sammelzeile von `fxWeekBuckets`. `info` ist entweder das echte `isoWeekInfo`-Ergebnis
 * oder der zweite, literal abweichende "leere Woche"-Rueckfall ohne `year` (siehe Eigenheit 2 im
 * Kopfkommentar).
 */
export interface FxWeekBucket {
  info: IsoWeekInfo | { key: string; start: string; end: string; week: string };
  dueUsd: number;
  securedUsd: number;
}

/** Eine Zeile der offenen Zahlungsposten einer Bestellung (Rueckgabe von `orderOpenPayments`). */
export interface OpenPaymentRow {
  order: Order;
  label: string;
  due: string;
  amt: number;
  eur: number;
  approx: boolean;
  phase: string;
  overdue: string | boolean;
}

// ================= Dollarkurs aus Zahlungen =================

/** index.html:5070 - Dollarkurs aus den Zahlstatus-Bloecken, bei zwei Zahlungen nach USD-Betrag gewichtet. */
export function paymentUsdRate(o: Order): number {
  const rows = ((o.payments || []) as OrderPayment[])
    .map((payment) => ({ amount: payAmount(o, payment), rate: Number(payment.usdRate) || 0 }))
    .filter((payment) => payment.rate > 0);
  if (!rows.length) return 0;
  const withAmount = rows.filter((payment) => payment.amount > 0);
  if (!withAmount.length) return rows.reduce((sum, payment) => sum + payment.rate, 0) / rows.length;
  const usd = withAmount.reduce((sum, payment) => sum + payment.amount, 0);
  const eur = withAmount.reduce((sum, payment) => sum + payment.amount / payment.rate, 0);
  return eur > 0 ? usd / eur : 0;
}

// ================= Kurssicherung (zentrales DTG-Register: DB.fxDeals) =================

/** index.html:5083 - Gueltiger Deal: Betrag und Kurs > 0. */
export function dealValid(d: FxDeal): boolean {
  return (Number(d.usd) || 0) > 0 && (Number(d.rate) || 0) > 0;
}

/**
 * index.html:5087 - An Bestellungen gebundener Anteil eines Deals. ABWEICHUNG vom Original
 * (Befund 9, Option B, entschieden 2026-08-11): `Array.isArray(...)` statt `|| []`. Das Original
 * laesst einen wahrhaftigen Nicht-Array nach `|| []` stehen und wirft an `.reduce`; ein einzelner
 * beschaedigter Deal legte damit die gesamte Kurssicherungsseite lahm.
 */
export function dealAllocated(d: FxDeal): number {
  return (Array.isArray(d.allocations) ? (d.allocations as DealAllocation[]) : []).reduce(
    (s, a) => s + (Number(a.usd) || 0),
    0,
  );
}

/** index.html:5091 - Frei verfuegbarer Rest eines Deals. */
export function dealFree(d: FxDeal): number {
  return Math.max(0, (Number(d.usd) || 0) - dealAllocated(d));
}

/**
 * index.html:5095 - Naechste laufende Nummer FX-###. `DB.fxDeals` ist hier das Argument `deals`.
 * Im Hub OHNE Aufrufer: die Sicherungsnummer vergibt der Server (`S-<n>`, Migration 057); die Funktion bleibt als
 * Portierungs-/Paritaetsbeleg (`test/fixtures/fx.test.ts`).
 */
export function nextFxNo(deals: readonly FxDeal[] | null | undefined): string {
  const max = (deals || []).reduce((m, d) => {
    const n = Number((String(d.no || "").match(/(\d+)\s*$/) || [])[1] || 0);
    return Math.max(m, n);
  }, 0);
  return "FX-" + String(max + 1).padStart(3, "0");
}

/**
 * index.html:5103 - Alle an eine Bestellung gebundenen Sicherungs-Teilbetraege. `DB.fxDeals` ist
 * hier das Argument `deals`.
 */
export function orderFx(o: Order, deals: readonly FxDeal[] | null | undefined): FxAllocation[] {
  return (deals || []).flatMap((d) =>
    dealValid(d)
      ? // Befund 9, Option B (2026-08-11) - siehe dealAllocated.
        (Array.isArray(d.allocations) ? (d.allocations as DealAllocation[]) : [])
          .filter((a) => a.orderId === o.id && (Number(a.usd) || 0) > 0)
          .map((a) => ({ deal: d, usd: Number(a.usd) }))
      : [],
  );
}

/**
 * index.html:5112 - Summen + Mischkurs: Gesamt-USD ÷ tatsaechlich bezahlte EUR (gewichtet).
 * `DB.fxDeals` ist hier das Argument `deals`.
 */
export function fxMix(o: Order, deals: readonly FxDeal[] | null | undefined): FxMix {
  const xs = orderFx(o, deals);
  const usd = xs.reduce((s, x) => s + x.usd, 0);
  const eur = xs.reduce((s, x) => s + x.usd / Number(x.deal.rate), 0);
  return { usd, eur, rate: eur > 0 ? usd / eur : 0 };
}

/**
 * index.html:5119 - Effektiver Kurs: Mischkurs der gebundenen Sicherungen, sonst manuelles
 * `usdRate`-Feld. `DB.fxDeals` ist hier das Argument `deals`.
 */
export function effRate(o: Order, deals: readonly FxDeal[] | null | undefined): number {
  const m = fxMix(o, deals);
  return m.rate > 0 ? m.rate : (paymentUsdRate(o) || Number(o.mixedUsdRate) || Number(o.usdRate) || 0);
}

// ================= Mischkursrechner =================

/** index.html:13190 */
export function parseFxDecimal(value: unknown): number {
  const normalized = String(value ?? "").trim().replace(/[%\s]/g, "").replace(",", ".");
  return Number(normalized) || 0;
}

/** index.html:13195 */
export function hasFxManualValue(value: unknown): boolean {
  return value !== "" && value !== null && value !== undefined;
}

/**
 * Ein Rechnerfeldwert im deutschen Zahlenformat (Komma statt Punkt, optional Punkt als
 * Tausendertrenner) - NEU (Auftrag "verbleibende Zahlenformat-Luecken auf deutsches Format
 * bringen", 2026-09-07). Ersetzt in `MischkursRechner.tsx`/`SicherungDetail.tsx` (frueher `SicherungsListe.tsx`) die bisherige
 * Kombination `Math.round(v * 10^n) / 10^n` + `String(...)`: die Rundung geht in `toLocaleString`
 * auf, ein zusaetzliches `Math.round` ist danach nicht mehr noetig. Bewusst OHNE
 * `minimumFractionDigits` - ein glatter Wert (z. B. 100) zeigt weiterhin "100", nicht "100,00"
 * (keine erzwungenen Nachkommanullen, entspricht dem bisherigen Verhalten mit `Math.round` +
 * `String`).
 *
 * `useGrouping` ist PFLICHT (kein Default) - Nachbesserung (2026-09-07) zum Rundreise-Bug: die
 * fuenf editierbaren Mischkursrechner-Felder (`MischkursRechner.tsx`) zeigen den Wert, der zuvor
 * ueber `onEntwurf` -> `App.tsx fxEntwurfAendern` -> `fxCalculatorValues` -> `parseFxDecimal`
 * zurueckgelaufen ist - der Rueckweg kennt nur `,` -> `.`, keinen Tausenderpunkt. Mit Gruppierung
 * riss "13.000" bei der naechsten Ziffer in zwei Zahlen auseinander (13 statt 13000) oder wurde zu
 * NaN/leer. Analog zu `orderListEditableAmount` (`lib/format.ts:262-267`, `useGrouping: false`),
 * derselbe Rundweg-Fall, dort bereits geloest. Die beiden schreibgeschuetzten Felder in
 * `SicherungDetail.tsx` haben keinen Rueckweg und bekommen bewusst `useGrouping: true`.
 */
export function fmtFxDecimal(value: number, maxFractionDigits: number, useGrouping: boolean): string {
  return value.toLocaleString("de-DE", { maximumFractionDigits: maxFractionDigits, useGrouping });
}

/**
 * index.html:13199 - Kernstueck des Mischkursrechners: Altguthaben plus neue USD ergeben Anteile und
 * einen gewichteten Mischkurs, jeder Wert per Override ueberschreibbar. Die Vorgabe ist hier `{}`
 * statt `fxCalculatorDraft || {}` - siehe Kopfkommentar.
 */
export function fxCalculatorValues(draft: FxCalculatorDraft = {}): FxCalculatorValues {
  const oldBalance = Math.max(0, parseFxDecimal(draft.oldBalance));
  const oldRate = Math.max(0, parseFxDecimal(draft.oldRate));
  const newUsd = Math.max(0, parseFxDecimal(draft.newUsd));
  const newRate = Math.max(0, parseFxDecimal(draft.newRate));
  const calculatedBalance = oldBalance + newUsd;
  const newBalance = hasFxManualValue(draft.newBalanceOverride)
    ? Math.max(0, parseFxDecimal(draft.newBalanceOverride))
    : calculatedBalance;
  const calculatedOldShare = newBalance > 0 ? oldBalance / newBalance : 0;
  const calculatedNewShare = newBalance > 0 ? newUsd / newBalance : 0;
  const oldShare = hasFxManualValue(draft.oldShareOverride)
    ? Math.max(0, parseFxDecimal(draft.oldShareOverride) / 100)
    : calculatedOldShare;
  const newShare = hasFxManualValue(draft.newShareOverride)
    ? Math.max(0, parseFxDecimal(draft.newShareOverride) / 100)
    : calculatedNewShare;
  const calculatedMixedRate =
    (oldRate > 0 ? oldShare * oldRate : 0) + (newRate > 0 ? newShare * newRate : 0);
  const mixedRate = hasFxManualValue(draft.mixedRateOverride)
    ? Math.max(0, parseFxDecimal(draft.mixedRateOverride))
    : calculatedMixedRate;
  const weightingPct = hasFxManualValue(draft.weightingOverride)
    ? Math.max(0, parseFxDecimal(draft.weightingOverride))
    : 100;
  return { oldBalance, oldRate, newUsd, newRate, newBalance, oldShare, newShare, mixedRate, weightingPct };
}

// ================= Wochenraster =================

/**
 * index.html:13380. Im Original liest sie das globale `DB.fxDeals` (Argument `deals`) und ruft das
 * globale `paymentCalendarRows()` auf (hier `paymentCalendarRows(orders, definitionen, suppliers)`
 * aus `lib/status.ts`, siehe Kopfkommentar). Ruft `todayIso()` auf - **parity-only, niemals in einer
 * Fixture.**
 */
export function fxWeekBuckets(
  deals: readonly FxDeal[] | null | undefined,
  definitionen: StatusDefinitionen,
  orders: readonly Order[],
  suppliers: readonly Supplier[] | null | undefined,
): FxWeekBucket[] {
  const map = new Map<string, FxWeekBucket>();
  const ensure = (info: FxWeekBucket["info"]): FxWeekBucket => {
    const key = info.key || "ohne";
    if (!map.has(key)) map.set(key, { info, dueUsd: 0, securedUsd: 0 });
    return map.get(key) as FxWeekBucket;
  };
  for (const row of paymentCalendarRows(orders, definitionen, suppliers)) {
    if (row.paid || row.usd <= 0) continue;
    const bucket = ensure(row.due ? isoWeekInfo(row.due) : { key: "ohne", start: "", end: "", week: "" });
    bucket.dueUsd += row.usd;
  }
  for (const deal of (deals || []).filter(dealValid)) {
    const bucket = ensure(
      deal.date ? isoWeekInfo(deal.date as string) : { key: "ohne", start: "", end: "", week: "" },
    );
    bucket.securedUsd += Number(deal.usd) || 0;
  }
  ensure(isoWeekInfo(todayIso()));
  const dated = [...map.values()]
    .filter((bucket) => bucket.info.start)
    .sort((a, b) => a.info.key.localeCompare(b.info.key));
  if (dated.length > 1) {
    const first = dated[0].info.start;
    const last = dated[dated.length - 1].info.start;
    // Im Original eine Date-Subtraktion (`new Date(...) - new Date(...)`); TypeScript erlaubt die
    // implizite Zahl-Koerzion zweier Date-Objekte nicht. `.getTime()` liefert denselben Zahlenwert
    // wie die von `valueOf()` erzwungene Original-Subtraktion - reine Typergaenzung.
    const weekSpan = Math.round(
      (new Date(`${last}T12:00:00`).getTime() - new Date(`${first}T12:00:00`).getTime()) / 604800000,
    );
    if (weekSpan <= 104) {
      for (let cursor = first; cursor <= last; cursor = addDays(cursor, 7)) ensure(isoWeekInfo(cursor));
    }
  }
  return [...map.values()].sort((a, b) => {
    if (a.info.key === "ohne") return 1;
    if (b.info.key === "ohne") return -1;
    return a.info.key.localeCompare(b.info.key);
  });
}

/** index.html:13413 */
export function fxWeekChartValue(value: unknown): string {
  const amount = Number(value) || 0;
  if (amount >= 1000000) return `$ ${NUM.format(Math.round(amount / 10000) / 100)} Mio.`;
  if (amount >= 1000) return `$ ${NUM.format(Math.round(amount / 100) / 10)} Tsd.`;
  return fmtUsd(amount);
}

// ================= Kosten (offene Zahlungen vor/nach ETD) =================

/**
 * index.html:13802 - Offene Zahlungsposten einer Bestellung inkl. Phase (vor/nach ETD) und
 * €-Naeherung. `DB.fxDeals` ist hier das Argument `deals`, `DB.suppliers` das Argument `suppliers`
 * (ueber `orderCurrency`). Siehe Kopfkommentar zur Verschiebung aus der Planliste von Task 5.
 */
export function orderOpenPayments(
  o: Order,
  deals: readonly FxDeal[] | null | undefined,
  suppliers: readonly Supplier[] | null | undefined,
): OpenPaymentRow[] {
  const rate = effRate(o, deals);
  const isUsd = orderCurrency(o, suppliers) === "USD";
  const toEur = (v: number): number => (isUsd ? (rate > 0 ? v / rate : v) : v);
  const approx = isUsd && rate <= 0;
  const today = todayIso();
  const phaseOf = (due: unknown): string =>
    o.etd && due ? ((due as string) < (o.etd as string) ? "vor" : "nach") : "nach";
  const rows: OpenPaymentRow[] = [];
  const ps = (o.payments || []) as OrderPayment[];
  if (ps.length) {
    ps.filter((p) => !p.paid).forEach((p) => {
      const amt = payAmount(o, p);
      if (amt <= 0) return;
      rows.push({
        order: o,
        label: (p.label as string) || "Rate",
        due: (p.due as string) || "",
        amt,
        eur: toEur(amt),
        approx,
        phase: phaseOf(p.due),
        overdue: (p.due as string) && (p.due as string) < today,
      });
    });
  } else if (!isPaid(o)) {
    const amt = orderTotal(o);
    if (amt > 0) {
      const due = nextDue(o) || "";
      rows.push({
        order: o,
        label: "Gesamtbetrag (kein Ratenplan)",
        due,
        amt,
        eur: toEur(amt),
        approx,
        phase: phaseOf(due),
        overdue: due && due < today,
      });
    }
  }
  return rows;
}
